<script>
import BoardResource from '@/resources/board'
import LocationResource from '@/resources/location'
import RankedRaceResource from '@/resources/rankedrace'
import RaceResultResource from '@/resources/raceresult'
import SideMenu from '../components/SideMenu.vue'
import messages from '@/mixins/messages'
import lucra from '@/mixins/lucra'
import { timeDateFormattedPretty } from '@/util'

export default {
    name: 'Boards',
    components: { SideMenu },
    mixins: [messages, lucra],
    computed: {
        openTeamRegistration() {
            return !!(this.board && this.board.attributes.open_team_registration)
        },
        teamSize() {
            return this.board ? this.board.attributes.team_size : null
        },
        teamsOpenToJoin() {
            const registration = this.board && this.board.attributes.team_registration
            return !!registration && !registration.full && !registration.locked
        },
        // A board with a cash tournament is excluded entirely, not just its promo panel,
        // whenever this site's location doesn't have cash tournaments enabled. Applies to
        // every board list (the rail menu, the mobile <select>, and default/route-id
        // selection below), so a hidden board is neither listed nor reachable by URL.
        visibleBoardResources() {
            return this.boardResources.filter((b) => !b.cashTournament || this.cashTournamentsLocationEnabled)
        },
        visibleBoardResourcesForLocation() {
            return this.boardResourcesForLocation.filter((b) => !b.cashTournament || this.cashTournamentsLocationEnabled)
        },
        // Cash tournaments take priority over everything else on this page: 1) any cash
        // tournament board, global ones before location ones, 2) any other global board,
        // 3) any other board scoped to the selected location. Splitting on cashTournament
        // first, then concatenating the two already-global-then-location-ordered source lists,
        // gets both the podium/rail order and the default board (boards[0]) to agree.
        cashBoardResources() {
            return this.visibleBoardResources.filter((b) => b.cashTournament)
        },
        cashBoardResourcesForLocation() {
            return this.visibleBoardResourcesForLocation.filter((b) => b.cashTournament)
        },
        nonCashBoardResources() {
            return this.visibleBoardResources.filter((b) => !b.cashTournament)
        },
        nonCashBoardResourcesForLocation() {
            return this.visibleBoardResourcesForLocation.filter((b) => !b.cashTournament)
        },
        boards() {
            return this.cashBoardResources.concat(this.cashBoardResourcesForLocation, this.nonCashBoardResources, this.nonCashBoardResourcesForLocation)
        },
        railGroups() {
            const toItem = (rowBoard) => ({
                key: rowBoard.id,
                label: String(rowBoard),
                board: rowBoard,
                active: !!this.board && rowBoard.id === this.board.id,
                icon: rowBoard.cashTournament ? 'trophy-fill' : undefined,
                accent: rowBoard.cashTournament ? 'gold' : undefined,
            })
            const groups = []
            const cashItems = this.cashBoardResources.concat(this.cashBoardResourcesForLocation).map(toItem)
            if (cashItems.length > 0) {
                groups.push({ label: 'Cash Tournaments', icon: 'cash-coin', accent: 'gold', items: cashItems })
            }
            if (this.nonCashBoardResources.length > 0) {
                groups.push({ label: 'Global', items: this.nonCashBoardResources.map(toItem) })
            }
            if (this.selectedLocationResource && this.nonCashBoardResourcesForLocation.length > 0) {
                groups.push({ label: String(this.selectedLocationResource), items: this.nonCashBoardResourcesForLocation.map(toItem) })
            }
            return groups
        },
        board() {
            return this.selectedBoardResource
        },
        ranksPodium() {
            return this.rankResources.slice(0, 3)
        },
        ranksRest() {
            return this.rankResources.slice(3)
        },
        isPoints() {
            return !!this.board && this.board.attributes.calculation === 'POINTS'
        },
        selectedRace() {
            return this.pastRaceResources.find((race) => race.id === this.selectedRaceId) || null
        },
        cashTournament() {
            return this.board ? this.board.cashTournament : null
        },
        windowLabel() {
            if (!this.board) {
                return ''
            }
            if (this.board.attributes.since) {
                return `Since ${this.timeDateFormattedPretty(this.board.attributes.since)}`
            }
            return this.board.attributes.time_frame_pretty || 'All time'
        },
        winchipWindow() {
            if (!this.board || this.board.attributes.since) {
                return ''
            }
            return this.board.attributes.time_frame_pretty || 'All time'
        },
        untilLabel() {
            if (!this.board) {
                return ''
            }
            if (this.board.attributes.since && new Date(this.board.attributes.since) > new Date()) {
                return `Tournament starts on ${this.timeDateFormattedPretty(this.board.attributes.since)}`
            }
            if (this.board.attributes.expires_at) {
                return `Goes until ${this.timeDateFormattedPretty(this.board.attributes.expires_at)}`
            }
            return ''
        },
        estTopPrize() {
            const ct = this.cashTournament
            if (!ct || !ct.joinable || ct.participant_count <= 2) {
                return null
            }
            const gross = (Number(ct.buy_in_amount_cents) / 100) * ct.participant_count
            const net = gross * (1 - Number(ct.fee_percent || 0) / 100)
            const first = ct.payout_structure && ct.payout_structure[0] ? Number(ct.payout_structure[0].value) : 100
            return net * (first / 100)
        },
        estTopPrizeTier() {
            if (this.estTopPrize == null) {
                return ''
            }
            if (this.estTopPrize > 500) {
                return 'boards__pot--mega'
            }
            if (this.estTopPrize > 100) {
                return 'boards__pot--big'
            }
            return ''
        },
        routeState() {
            const route = { name: 'leaderboards', params: { id: this.selectedBoardResource.id }, query: {} }
            if (this.selectedLocationResource) {
                route.query.location = this.selectedLocationResource.id
            }
            return route
        },
    },
    watch: {
        async selectedLocationResource() {
            await this.updateLocationBoards()
            await this.search()
            this.$router.replace(this.routeState).catch(() => {})
        },
    },
    data() {
        return {
            initializing: true,
            loading: true,
            boardResources: [],
            boardResourcesForLocation: [],
            selectedBoardResource: null,
            locationResources: [],
            selectedLocationResource: null,
            rankResources: [],
            upcomingRaceResources: [],
            pastRaceResources: [],
            selectedRaceId: null,
            raceResultResources: [],
            loadingRaceResults: false,
            joining: false,
            sharingStandings: false,
            shareLogoImage: null,
        }
    },
    methods: {
        timeDateFormattedPretty,
        async joinCash() {
            // Joining a cash tournament always needs a real, logged-in account (Lucra identity
            // linking, KYC, payouts all hang off the user record), so this is gated here rather
            // than relying on the button's own disabled state to keep an anonymous visitor out.
            if (!this.$store.getters.token) {
                this.$router.push({ name: 'signup', query: { redirect: this.$route.fullPath } })
                return
            }
            this.joining = true
            // Held back until the iframe is actually closed. It sits at z-index 3000 while
            // open, so a message added while it's still up renders underneath it, unseen.
            let pendingMessage = null
            try {
                await this.$lucraPrepareDeposit()
                await this.$lucraOpen(this.$refs.lucraContainer, { hidden: true })
                await this.$lucraLinkUser()
                let result = await this.$lucraJoinTournament(this.cashTournament.provider_matchup_id)
                if (result.needsDeposit) {
                    pendingMessage = ['info', 'Add funds to cover the buy-in, then select join again.']
                }
                if (result.locationError) {
                    pendingMessage = ['danger', 'We could not verify your location for a cash tournament. Try again with location services on.']
                } else if (result.joined) {
                    await this.selectedBoardResource.joinCashTournament()
                    pendingMessage = ['success', "You're in the tournament. Set laps at the simulator to climb the board."]
                    // useCache: false, since detail() otherwise serves the 10s client-side
                    // resource cache, so this board (already cached from the initial page
                    // load) would come back with the pre-join, not-yet-joined state.
                    await this.onSelectBoard(await BoardResource.detail(this.selectedBoardResource.id, { useCache: false }))
                }
            } catch (e) {
                pendingMessage = ['danger', this.getResponseMessageFromAxiosException(e)]
            }
            await this.$lucraCloseRoute()
            if (pendingMessage) {
                this.addMessage(...pendingMessage)
            }
            this.joining = false
        },
        rankLabel(item) {
            return item.attributes.name || item.attributes.display_name
        },
        rankValue(item) {
            return this.isPoints ? `${item.attributes.points} PTS` : item.attributes.hms
        },
        rankSub(item) {
            return item.attributes.car_name || item.attributes.car || ''
        },
        async search() {
            this.loading = true
            this.rankResources = []
            const result = await this.selectedBoardResource.getResults()
            this.rankResources = result.resources
            this.upcomingRaceResources = await this.selectedBoardResource.getUpcomingRaces()
            await this.loadPastRaces()
            this.loading = false
        },
        async loadPastRaces() {
            this.pastRaceResources = []
            this.selectedRaceId = null
            this.raceResultResources = []
            if (!this.isPoints) {
                return
            }
            const now = new Date()
            const result = await RankedRaceResource.filter({ board: this.selectedBoardResource.id, page_size: 100, ordering: '-date_time' })
            this.pastRaceResources = (result.resources || []).filter((race) => race.dateTime && race.dateTime <= now)
            if (this.pastRaceResources.length > 0) {
                await this.selectRace(this.pastRaceResources[0].id)
            }
        },
        async selectRace(raceId) {
            this.selectedRaceId = raceId
            this.loadingRaceResults = true
            const result = await RaceResultResource.filter({ race: raceId, page_size: 100, ordering: 'position' })
            this.raceResultResources = result.resources
            this.loadingRaceResults = false
        },
        async updateLocationBoards() {
            if (this.selectedLocationResource) {
                this.boardResourcesForLocation = await BoardResource.filterAll({ location: this.selectedLocationResource.id })
            } else {
                this.boardResourcesForLocation = []
            }
        },
        async onSelectBoard(board) {
            this.selectedBoardResource = board
            if (this.selectedBoardResource.id !== this.$route.params.id) {
                this.$router.replace(this.routeState).catch(() => {})
            }
            if (this.selectedLocationResource) {
                await this.updateLocationBoards()
            }
            await this.search()
        },
        // Shares the current top-of-board standings as one image, generated client-side on a
        // <canvas> (no backend endpoint, nothing stored). There's no public API for posting
        // straight into Instagram from a plain website; navigator.share() hands the image to
        // the visitor's OS-level share sheet, where Instagram (Story or Post/DM) is one of the
        // apps they can pick if it's installed, the same mechanism every other mobile app uses
        // for this. On a desktop browser, or a mobile browser without file-sharing support, it
        // downloads the image instead with a message explaining Instagram itself needs the app
        // to post from.
        async shareStandings() {
            this.sharingStandings = true
            try {
                const blob = await this.renderShareImage()
                const safeBoardName = String(this.board || 'standings').replace(/[^a-z0-9]+/gi, '-')
                const fileName = `${safeBoardName}-standings.png`
                const file = typeof File !== 'undefined' ? new File([blob], fileName, { type: 'image/png' }) : null
                const leader = this.ranksPodium[0]
                const shareText = leader ? `${String(this.board)} standings: ${this.rankLabel(leader)} leads with ${this.rankValue(leader)}!` : `${String(this.board)} standings`
                if (file && navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
                    try {
                        await navigator.share({ files: [file], title: String(this.board), text: shareText })
                        return
                    } catch (e) {
                        if (e && e.name === 'AbortError') {
                            return
                        }
                        // Sharing failed (unsupported target, permission, etc.) - fall through
                        // to the download fallback below rather than leaving the racer stuck.
                    }
                }
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = fileName
                document.body.appendChild(a)
                a.click()
                a.remove()
                URL.revokeObjectURL(url)
                this.addMessage('success', 'Image downloaded. Open Instagram on your phone and share it as a Story or Post from your camera roll.')
            } catch (e) {
                this.addMessage('danger', 'Could not create a shareable image.')
            } finally {
                this.sharingStandings = false
            }
        },
        loadImage(src) {
            return new Promise((resolve, reject) => {
                const img = new Image()
                img.crossOrigin = 'anonymous'
                img.onload = () => resolve(img)
                img.onerror = reject
                img.src = src
            })
        },
        // Shortens text with an ellipsis instead of wrapping, so every element on the card
        // keeps its fixed vertical position regardless of how long a board or racer name is.
        fitCanvasText(ctx, text, maxWidth) {
            let str = String(text == null ? '' : text)
            if (ctx.measureText(str).width <= maxWidth) {
                return str
            }
            while (str.length > 1 && ctx.measureText(`${str}…`).width > maxWidth) {
                str = str.slice(0, -1)
            }
            return `${str}…`
        },
        // Traces a rounded-rect path (no native ctx.roundRect() dependency, so this
        // draws identically on every mobile browser this card gets shared from).
        roundRectPath(ctx, x, y, w, h, r) {
            ctx.beginPath()
            ctx.moveTo(x + r, y)
            ctx.arcTo(x + w, y, x + w, y + h, r)
            ctx.arcTo(x + w, y + h, x, y + h, r)
            ctx.arcTo(x, y + h, x, y, r)
            ctx.arcTo(x, y, x + w, y, r)
            ctx.closePath()
        },
        async renderShareImage() {
            if (document.fonts && document.fonts.ready) {
                await document.fonts.ready
            }
            const width = 1080
            const height = 1920
            const margin = 72
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')
            // Reads the live, already-theme-and-location-aware CSS custom properties (branding.js
            // writes the location's primary/secondary/theme onto these same tokens the rest of
            // the app renders with), so this card always matches whatever mode and whitelabel
            // colors the visitor is actually looking at. Fallbacks only cover a pre-hydration edge case.
            const style = getComputedStyle(document.documentElement)
            const cssVar = (name, fallback) => style.getPropertyValue(name).trim() || fallback
            const bg = cssVar('--bg', '#0b0b0d')
            const panel = cssVar('--panel', '#161619')
            const panel2 = cssVar('--panel-2', '#1c1c20')
            const line = cssVar('--line', '#2a2a2e')
            const line2 = cssVar('--line-2', line)
            const action = cssVar('--action', '#e0332e')
            const teal = cssVar('--teal', '#5ae1da')
            const gold = cssVar('--sos-gold', '#d4af37')
            const ink = cssVar('--text-heading', '#ffffff')
            const muted = cssVar('--muted', '#9a9aa2')
            const fontDisplay = cssVar('--font-display', 'sans-serif')
            const fontBody = cssVar('--font-body', 'sans-serif')
            const rowColors = [gold, '#c7c9d1', '#c98a4b']
            const cardRadius = 18

            ctx.fillStyle = bg
            ctx.fillRect(0, 0, width, height)

            // Logo, top left, the same corner it sits in on every other screen (Nav.vue). Drawn
            // as its own row (not beside the title) and contained to a fixed box, since a wide
            // wordmark logo at a fixed height would otherwise eat unpredictable amounts of the
            // title's width depending on the location's own logo shape.
            let kickerY = 78
            if (this.shareLogoImage) {
                const maxLogoW = 220
                const maxLogoH = 64
                const ratio = this.shareLogoImage.width / this.shareLogoImage.height
                let logoWidth = maxLogoW
                let logoHeight = logoWidth / ratio
                if (logoHeight > maxLogoH) {
                    logoHeight = maxLogoH
                    logoWidth = logoHeight * ratio
                }
                ctx.drawImage(this.shareLogoImage, margin, 56, logoWidth, logoHeight)
                kickerY = 56 + maxLogoH + 30
            }

            // Kicker mark: the same two-tone accent HeaderImage.vue's pagehead__mark draws
            // (lead colour 0-62%, a gap, chase colour 72-100%), sized as a small accent next
            // to the label instead of the full-width bar this used to render at.
            const markW = 84
            const markH = 12
            ctx.fillStyle = action
            ctx.fillRect(margin, kickerY, markW * 0.62, markH)
            ctx.fillStyle = teal
            ctx.fillRect(margin + markW * 0.72, kickerY, markW * 0.28, markH)

            ctx.textAlign = 'left'
            ctx.fillStyle = teal
            ctx.font = `700 26px ${fontDisplay}`
            ctx.fillText(this.isPoints ? 'CHAMPIONSHIP STANDINGS' : 'HOT LAP STANDINGS', margin + markW + 14, kickerY + markH + 8)

            const titleY = kickerY + 92
            ctx.fillStyle = ink
            ctx.font = `700 56px ${fontDisplay}`
            ctx.fillText(this.fitCanvasText(ctx, String(this.board || ''), width - margin * 2), margin, titleY)

            // One row per podium finisher: rank badge, name, value, each in its own panel.
            const rows = this.ranksPodium.slice(0, 3)
            const rowHeight = 148
            const rowGap = 14
            const rowWidth = width - margin * 2
            const rowX = margin
            let rowY = titleY + 70
            rows.forEach((item, i) => {
                const accent = rowColors[i] || muted
                this.roundRectPath(ctx, rowX, rowY, rowWidth, rowHeight, cardRadius)
                ctx.fillStyle = panel2
                ctx.fill()
                ctx.save()
                ctx.clip()
                ctx.fillStyle = accent
                ctx.fillRect(rowX, rowY, 8, rowHeight)
                ctx.restore()
                ctx.strokeStyle = line
                ctx.lineWidth = 2
                this.roundRectPath(ctx, rowX, rowY, rowWidth, rowHeight, cardRadius)
                ctx.stroke()

                ctx.textAlign = 'left'
                ctx.fillStyle = accent
                ctx.font = `800 74px ${fontDisplay}`
                ctx.fillText(`P${item.attributes.rank}`, rowX + 40, rowY + 96)

                ctx.fillStyle = ink
                ctx.font = `700 42px ${fontDisplay}`
                ctx.fillText(this.fitCanvasText(ctx, this.rankLabel(item), rowWidth - 260), rowX + 220, rowY + 64)

                ctx.fillStyle = teal
                ctx.font = `700 36px ${fontBody}`
                const valueText = this.fitCanvasText(ctx, this.rankValue(item), rowWidth - 260)
                ctx.fillText(valueText, rowX + 220, rowY + 110)

                const car = this.rankSub(item)
                if (car) {
                    const valueWidth = ctx.measureText(valueText).width
                    ctx.fillStyle = muted
                    ctx.font = `500 28px ${fontBody}`
                    ctx.fillText(this.fitCanvasText(ctx, ` · ${car}`, rowWidth - 260 - valueWidth - 16), rowX + 220 + valueWidth + 16, rowY + 110)
                }

                rowY += rowHeight + rowGap
            })

            // Positions 4-10, as a compact list beneath the podium (same pos / name / value
            // shape as the sa-lb standings table on the page, just condensed).
            const restRows = this.ranksRest.slice(0, 7)
            if (restRows.length > 0) {
                let listY = rowY + 20
                ctx.fillStyle = muted
                ctx.font = `700 22px ${fontDisplay}`
                ctx.fillText('FULL STANDINGS', rowX, listY)
                listY += 30
                ctx.strokeStyle = line
                ctx.lineWidth = 1
                ctx.beginPath()
                ctx.moveTo(rowX, listY)
                ctx.lineTo(rowX + rowWidth, listY)
                ctx.stroke()

                const listRowH = 52
                restRows.forEach((item, i) => {
                    const y = listY + i * listRowH
                    const textY = y + listRowH - 16

                    ctx.textAlign = 'left'
                    ctx.fillStyle = muted
                    ctx.font = `700 26px ${fontBody}`
                    ctx.fillText(String(item.attributes.rank).padStart(2, '0'), rowX, textY)

                    const car = this.rankSub(item)
                    const label = car ? `${this.rankLabel(item)}  ·  ${car}` : this.rankLabel(item)
                    ctx.fillStyle = ink
                    ctx.font = `600 26px ${fontBody}`
                    ctx.fillText(this.fitCanvasText(ctx, label, rowWidth - 230), rowX + 70, textY)

                    ctx.textAlign = 'right'
                    ctx.fillStyle = teal
                    ctx.font = `700 26px ${fontBody}`
                    ctx.fillText(this.rankValue(item), rowX + rowWidth, textY)

                    ctx.strokeStyle = line2
                    ctx.globalAlpha = 0.6
                    ctx.beginPath()
                    ctx.moveTo(rowX, y + listRowH)
                    ctx.lineTo(rowX + rowWidth, y + listRowH)
                    ctx.stroke()
                    ctx.globalAlpha = 1
                })
            }

            ctx.textAlign = 'center'
            ctx.fillStyle = muted
            ctx.font = `500 34px ${fontBody}`
            ctx.fillText('Think you can beat this?', width / 2, height - 180)
            ctx.fillStyle = ink
            ctx.font = `700 42px ${fontDisplay}`
            ctx.fillText('Race it yourself.', width / 2, height - 128)

            return new Promise((resolve, reject) => {
                canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png')
            })
        },
        // Kicked off once on mount, never awaited by the share flow itself: the branding logo
        // is a remote image, and any network wait between a click and navigator.share() risks
        // losing the user-gesture context Safari requires for the share sheet to open at all.
        async preloadShareLogo() {
            try {
                const logoUrl = this.$branding.storeVal('design_logo_image_url', 'https://drivesims-assets-public.s3.us-west-1.amazonaws.com/images/shiftarcade-logo-web.png')
                // The S3 buckets these logos live in send no Access-Control-Allow-Origin header,
                // so a canvas can draw the image but is refused export (toBlob) - the browser
                // treats it as tainted. Routing through our own API re-serves the same bytes with
                // this API's own CORS headers (CORS_ALLOW_ALL_ORIGINS in driveos), which is the
                // only way to make the image usable for a canvas export in any browser.
                const proxiedUrl = `${this.$appConfig('API_BASE_URL')}/images/proxy/?src=${encodeURIComponent(logoUrl)}`
                this.shareLogoImage = await this.loadImage(proxiedUrl)
            } catch (e) {
                this.shareLogoImage = null
            }
        },
    },
    async mounted() {
        this.preloadShareLogo()

        const [locations, boards] = await Promise.all([LocationResource.all(), BoardResource.filterAll({ location__isnull: true })])
        this.locationResources = locations
        this.boardResources = boards

        if (this.$route.query.location) {
            this.selectedLocationResource = this.locationResources.find((resource) => resource.id == this.$route.query.location)
        } else if (this.locationResources.length > 0) {
            this.selectedLocationResource = this.locationResources[0]
        }

        await this.updateLocationBoards()

        if (this.$route.params.id) {
            // A direct link to a now-hidden cash board (see visibleBoardResources above)
            // falls back to the first visible board instead of rendering nothing.
            this.selectedBoardResource = this.boards.find((resource) => resource.id == this.$route.params.id) || this.boards[0]
        } else {
            this.selectedBoardResource = this.boards[0]
        }

        this.loading = false
        this.initializing = false
        await this.search()
    },
}
</script>

<template>
    <div class="sa-page" v-if="!initializing">
        <header-image :kicker="(isPoints ? 'Campeonato' : 'Vuelta rápida') + ' - Clasificación'" :src="board && board.attributes.image_url ? board.attributes.image_url : ''">
            <h1>{{ board ? String(board) : 'Tablas' }}</h1>
            <template #pic>
                <span v-if="cashTournament && cashTournamentsLocationEnabled" class="boards__winchip-cash d-inline-flex align-items-center text-uppercase rounded-pill"> <b-icon icon="trophy-fill" scale="0.7" /> ${{ (cashTournament.buy_in_amount_cents / 100).toFixed(0) }} en efectivo </span>
                <span v-if="winchipWindow" class="boards__winchip-label text-uppercase text-truncate font-weight-bold">{{ winchipWindow }}</span>
            </template>
        </header-image>
        <div class="sa-page__inner">
            <messages />

            <div class="sa-toolbar" v-if="locationResources.length > 0 || ranksPodium.length > 0">
                <template v-if="locationResources.length > 0">
                    <span class="sa-overline mr-2">Ubicación</span>
                    <b-form-select v-model="selectedLocationResource" size="sm" class="boards__pick text-uppercase">
                        <b-form-select-option :value="null">Global</b-form-select-option>
                        <b-form-select-option v-for="location in locationResources" :key="location.id" :value="location">{{ location }}</b-form-select-option>
                    </b-form-select>
                </template>
                <span class="sa-toolbar__spacer"></span>
                <b-button v-if="ranksPodium.length > 0" variant="primary" size="sm" :disabled="sharingStandings" @click="shareStandings">
                    <b-icon :icon="sharingStandings ? 'arrow-repeat' : 'instagram'" :animation="sharingStandings ? 'spin' : null" class="mr-1" />
                    {{ sharingStandings ? 'Preparando…' : 'Compartir clasificación' }}
                </b-button>
            </div>

            <div class="sa-railpage" style="--sa-rail-w: 300px">
                <side-menu :groups="railGroups" @select="(item) => onSelectBoard(item.board)" />

                <div v-if="board && board.attributes.image_url" class="boards__mobile-pic d-lg-none" :style="{ backgroundImage: 'url(' + board.attributes.image_url + ')' }">
                    <span v-if="cashTournament && cashTournamentsLocationEnabled" class="boards__winchip-cash d-inline-flex align-items-center text-uppercase rounded-pill"> <b-icon icon="trophy-fill" scale="0.7" /> ${{ (cashTournament.buy_in_amount_cents / 100).toFixed(0) }} en efectivo </span>
                    <span v-if="winchipWindow" class="boards__winchip-label text-uppercase text-truncate font-weight-bold">{{ winchipWindow }}</span>
                </div>

                <section class="sa-railpage__body pt-0">
                    <div class="boards__podium mb-4" v-if="ranksPodium.length > 0">
                        <div v-for="item in ranksPodium" :key="item.id" class="boards__slot d-flex flex-column" :class="`boards__slot--${item.attributes.rank}`">
                            <b-icon icon="trophy-fill" class="boards__trophy" />
                            <span class="boards__slotpos font-weight-bold">P{{ item.attributes.rank }}</span>
                            <span class="boards__slotname text-uppercase text-truncate font-weight-bold">
                                {{ rankLabel(item) }}
                                <a v-if="item.attributes.display_social" class="boards__social sa-mono text-muted ml-2" :href="`https://instagram.com/${item.attributes.display_social}`" target="_blank">@{{ item.attributes.display_social }}</a>
                            </span>
                            <span class="boards__slottime sa-mono">{{ rankValue(item) }}</span>
                            <span class="boards__slotcar sa-mono text-muted text-truncate" v-if="rankSub(item)">{{ rankSub(item) }}</span>
                        </div>
                    </div>
                    <div class="sa-empty mb-4" v-else-if="!loading">Aún no se han registrado vueltas en esta tabla.</div>
                    <b-skeleton-table :rows="3" class="mb-4" v-else />

                    <div class="sa-card boards__info mb-4" v-if="board">
                        <div class="d-flex flex-wrap boards__cash-stats" :class="{ 'mb-2': board.attributes.description }">
                            <span class="boards__cash-stat d-inline-flex align-items-center text-nowrap text-muted"><b-icon icon="calendar3" class="mr-1" />{{ windowLabel }}</span>
                            <span class="boards__cash-stat d-inline-flex align-items-center text-nowrap text-muted" v-if="untilLabel"><b-icon icon="hourglass-split" class="mr-1" />{{ untilLabel }}</span>
                        </div>
                        <p class="boards__desc text-muted mb-0" v-if="board.attributes.description" v-html="board.attributes.description"></p>
                        <div class="boards__cash-team d-flex flex-wrap align-items-center justify-content-between mt-3 pt-3" v-if="openTeamRegistration && !cashTournament && (board.attributes.user_team_name || teamsOpenToJoin)">
                            <span class="text-dark font-weight-bold mr-3 my-1" v-if="board.attributes.user_team_name"
                                >Compites para <strong>{{ board.attributes.user_team_name }}</strong
                                >.</span
                            >
                            <span class="text-dark font-weight-bold mr-3 my-1" v-else>Esta serie se disputa en equipos{{ teamSize ? ` de ${teamSize}` : '' }}. Crea un equipo e invita a tus amigos, o únete a uno abierto.</span>
                            <b-button variant="primary" size="sm" class="my-1" :to="{ name: 'accountTeams', query: { board: board.id } }">{{ board.attributes.user_team_name ? 'Gestionar equipo' : 'Unirse' }}</b-button>
                        </div>
                    </div>

                    <div class="sa-card boards__cash mb-4" v-if="cashTournament && cashTournamentsLocationEnabled">
                        <div class="d-flex flex-wrap align-items-start justify-content-between boards__cash-head mb-3">
                            <div class="min-width-0">
                                <p class="sa-overline mb-1"><b-icon icon="trophy-fill" class="mr-1" />Torneo con premio en efectivo</p>
                                <div class="boards__cash-title text-truncate font-weight-bold">{{ cashTournament.title }}</div>
                            </div>
                            <div class="flex-shrink-0">
                                <b-badge variant="success" v-if="cashTournament.joined">Estás dentro</b-badge>
                                <b-button v-else-if="cashTournament.joinable" variant="primary" size="sm" :disabled="joining" @click="joinCash"> <b-spinner small v-if="joining" /> {{ lucraNeedsDeposit ? 'Añadir fondos y unirse' : 'Unirse al torneo' }} </b-button>
                                <b-badge variant="light" v-else>Próximamente</b-badge>
                            </div>
                        </div>

                        <p class="boards__cash-pitch py-2 px-3 mb-3">
                            <b-icon icon="cash-stack" class="mr-1" />Este es un torneo con dinero real. Los primeros clasificados se reparten un premio real en efectivo, no puntos ni crédito de la tienda, y solo está
                            disponible para pilotos en ubicaciones participantes.
                        </p>

                        <div class="d-flex flex-wrap boards__cash-stats">
                            <span class="boards__cash-stat d-inline-flex align-items-center text-nowrap text-muted"><b-icon icon="cash-coin" class="mr-1" />${{ (cashTournament.buy_in_amount_cents / 100).toFixed(2) }} de inscripción</span>
                            <span class="boards__cash-stat d-inline-flex align-items-center text-nowrap text-muted" v-if="cashTournament.participant_count > 1"><b-icon icon="people-fill" class="mr-1" />{{ cashTournament.participant_count }} compitiendo</span>
                            <span class="boards__cash-stat d-inline-flex align-items-center text-nowrap text-muted" v-if="teamSize"><b-icon icon="flag-fill" class="mr-1" />Equipos de {{ teamSize }}</span>
                            <span v-if="estTopPrize != null" class="boards__pot d-inline-flex align-items-center rounded-pill font-weight-bold" :class="estTopPrizeTier">
                                <b-icon icon="trophy-fill" scale="0.8" class="mr-1" /> ~${{ estTopPrize.toLocaleString(undefined, { maximumFractionDigits: 0 }) }} en efectivo para el 1.º
                            </span>
                        </div>

                        <div class="boards__cash-team mt-3 pt-3 text-muted" v-if="openTeamRegistration && (cashTournament.user_team_name || teamsOpenToJoin)">
                            <span v-if="cashTournament.user_team_name"
                                >Compites para <strong>{{ cashTournament.user_team_name }}</strong
                                >.</span
                            >
                            <span v-else-if="cashTournament.joined">Te has unido al torneo. Ahora crea un equipo o únete a uno.</span>
                            <span v-else>Únete al torneo de arriba y después crea un equipo o únete a uno.</span>
                            <router-link class="ml-1" :to="{ name: 'accountTeams', query: { board: board.id } }">{{ cashTournament.user_team_name ? 'Gestionar equipo' : 'Unirse' }}</router-link>
                        </div>
                    </div>

                    <div class="sa-lb" v-if="ranksRest.length > 0">
                        <div class="sa-lb__head boards__lbhead">
                            <span>Pos.</span>
                            <span>Piloto</span>
                            <span class="d-none d-sm-block">Coche</span>
                            <span>{{ isPoints ? 'Puntos' : 'Mejor vuelta' }}</span>
                        </div>
                        <div class="sa-lb__row boards__row" v-for="item in ranksRest" :key="item.id">
                            <span class="sa-lb__pos">{{ String(item.attributes.rank).padStart(2, '0') }}</span>
                            <span class="sa-lb__driver">
                                {{ rankLabel(item) }}
                                <a v-if="item.attributes.display_social" class="boards__social sa-mono text-muted ml-2" :href="`https://instagram.com/${item.attributes.display_social}`" target="_blank">@{{ item.attributes.display_social }}</a>
                            </span>
                            <span class="sa-mono small text-muted d-none d-sm-block">{{ rankSub(item) }}</span>
                            <span class="sa-lb__num">{{ rankValue(item) }}</span>
                        </div>
                    </div>

                    <b-card no-body class="mt-4" v-if="isPoints && (upcomingRaceResources.length > 0 || pastRaceResources.length > 0)">
                        <b-tabs card>
                            <b-tab>
                                <template #title><b-icon icon="clock-history" class="mr-1" />Resultados</template>

                                <template v-if="pastRaceResources.length > 0">
                                    <div class="d-flex flex-wrap boards__race-pills mb-3">
                                        <button
                                            v-for="race in pastRaceResources"
                                            :key="race.id"
                                            type="button"
                                            class="sa-chip boards__race-pill"
                                            :class="{ 'is-active': race.id === selectedRaceId }"
                                            @click="selectRace(race.id)"
                                        >
                                            {{ race }}
                                        </button>
                                    </div>

                                    <div class="small text-muted mb-2" v-if="selectedRace">{{ timeDateFormattedPretty(selectedRace.attributes.date_time) }}</div>

                                    <div class="sa-lb" v-if="raceResultResources.length > 0">
                                        <div class="sa-lb__head boards__lbhead">
                                            <span>Pos.</span>
                                            <span>Piloto</span>
                                            <span class="d-none d-sm-block">Coche</span>
                                            <span>Puntos</span>
                                        </div>
                                        <div class="sa-lb__row boards__row" v-for="item in raceResultResources" :key="item.id">
                                            <span class="sa-lb__pos">{{ String(item.attributes.position).padStart(2, '0') }}</span>
                                            <span class="sa-lb__driver"
                                                >{{ item.get('display_name') }}
                                                <b-icon v-if="item.attributes.fastest_lap" icon="stopwatch-fill" class="text-info ml-1" v-b-tooltip.hover title="Vuelta más rápida" />
                                            </span>
                                            <span class="sa-mono small text-muted d-none d-sm-block">{{ item.attributes.car }}</span>
                                            <span class="sa-lb__num">{{ item.attributes.points }} PTS</span>
                                        </div>
                                    </div>
                                    <b-skeleton-table :rows="4" v-else-if="loadingRaceResults" />
                                    <div class="sa-empty" v-else>Aún no se han registrado resultados para esta carrera.</div>
                                </template>
                                <div class="sa-empty" v-else>Aún no se ha completado ninguna carrera.</div>
                            </b-tab>

                            <b-tab>
                                <template #title><b-icon icon="calendar-event" class="mr-1" />Próximas</template>

                                <template v-if="upcomingRaceResources.length > 0">
                                    <div
                                        class="boards__upcoming-row d-flex align-items-baseline justify-content-between py-2"
                                        :class="{ 'border-0 pb-0': index === upcomingRaceResources.length - 1 }"
                                        v-for="(race, index) in upcomingRaceResources"
                                        :key="race.id"
                                    >
                                        <span class="text-dark">{{ race }}</span>
                                        <span class="text-muted sa-mono">{{ timeDateFormattedPretty(race.attributes.date_time) }}</span>
                                    </div>
                                </template>
                                <div class="sa-empty" v-else>Aún no hay carreras programadas.</div>
                            </b-tab>
                        </b-tabs>
                    </b-card>
                </section>
            </div>
        </div>
        <div ref="lucraContainer" class="boards__lucra"></div>
    </div>
</template>

<style lang="scss" scoped>
.boards__pick {
    width: auto;
    min-width: 190px;
    font-family: var(--font-display);
    font-weight: 600;
    letter-spacing: 0.06em;
    font-size: 13px;
}

.boards__desc {
    font-size: 14px;
    line-height: 1.6;
}

// Cash tournament hero card: same gold-accent treatment Grid.vue's cash board cards use.
.boards__cash {
    border-color: color-mix(in srgb, var(--sos-gold) 45%, transparent);
}
.boards__cash-head {
    gap: 12px;
}
.boards__cash-title {
    font-family: var(--font-display);
    font-size: 19px;
    color: var(--text-heading);
}
.boards__cash-pitch {
    font-size: 14px;
    line-height: 1.5;
    color: var(--ink);
    background: color-mix(in srgb, var(--sos-gold) 10%, transparent);
    border: 1px solid color-mix(in srgb, var(--sos-gold) 30%, transparent);
    border-radius: var(--r-sm);
}
.boards__cash-pitch .b-icon {
    color: var(--sos-gold);
}
.boards__cash-stats {
    gap: 10px 18px;
}
.boards__cash-stat {
    font-size: 13px;
}
.boards__cash-team {
    border-top: 1px solid var(--line);
    font-size: 13px;
}

.boards__upcoming-row {
    gap: 12px;
    border-bottom: 1px solid var(--line);
    font-size: 14px;
}

// Podium: top 3, level with the first rail cell, evenly gridded.
.boards__podium {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
}
@media (max-width: 560px) {
    .boards__podium {
        grid-template-columns: 1fr;
    }
}
.boards__slot {
    position: relative;
    gap: 5px;
    min-width: 0;
    background: var(--panel);
    border: 1px solid var(--line);
    border-top: 3px solid var(--line-2);
    border-radius: var(--r-lg);
    padding: 16px 18px 18px;
}
.boards__trophy {
    display: none;
}
.boards__slot--1 {
    border-top-color: var(--action);
}
.boards__slot--1 .boards__trophy {
    display: block;
    position: absolute;
    top: 12px;
    right: 14px;
    color: var(--sos-gold);
    font-size: 16px;
}
.boards__slot--2 {
    border-top-color: var(--line-2);
}
.boards__slotpos {
    font-family: var(--font-display);
    font-size: 26px;
    line-height: 1;
    color: var(--action);
}
.boards__slot--1 .boards__slotpos {
    font-size: 32px;
}
.boards__slotname {
    font-family: var(--font-display);
    font-size: 15px;
    letter-spacing: 0.02em;
    color: var(--text-heading);
}
.boards__slottime {
    font-size: 17px;
    color: var(--text-heading);
}
.boards__slotcar {
    font-size: 11px;
}

.boards__lbhead,
.boards__row {
    grid-template-columns: 48px minmax(0, 1fr) auto 120px;
    gap: 16px;
}
.boards__race-pills {
    gap: 8px;
}
.boards__race-pill {
    cursor: pointer;
}
.boards__row .sa-lb__pos {
    color: var(--action);
}
.boards__social {
    font-size: 10px;
}

.boards__pot {
    padding: 2px 9px;
    font-size: 11px;
    letter-spacing: 0.02em;
    white-space: nowrap;
    color: var(--teal);
    background: color-mix(in srgb, var(--teal) 15%, transparent);
    border: 1px solid color-mix(in srgb, var(--teal) 45%, transparent);
}
.boards__pot--big {
    font-size: 12px;
    color: var(--black);
    background: var(--sos-gold);
    border-color: transparent;
    box-shadow: 0 2px 10px color-mix(in srgb, var(--sos-gold) 55%, transparent);
}
.boards__pot--mega {
    font-size: 13px;
    padding: 4px 12px;
    color: var(--black);
    background: linear-gradient(100deg, var(--sos-gold), #ffe58f 45%, var(--sos-gold));
    border-color: transparent;
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--sos-gold) 35%, transparent), 0 4px 20px color-mix(in srgb, var(--sos-gold) 70%, transparent);
    animation: boards-pot-pulse 1.8s ease-in-out infinite;
}
@keyframes boards-pot-pulse {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.045);
    }
}
@media (prefers-reduced-motion: reduce) {
    .boards__pot--mega {
        animation: none;
    }
}

.boards__winchip-cash {
    position: absolute;
    top: 7px;
    left: 7px;
    gap: 3px;
    padding: 2px 7px;
    background: var(--sos-gold);
    color: var(--black);
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.05em;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
.boards__winchip-label {
    position: relative;
    color: var(--white);
    line-height: 1.2;
    max-width: 100%;
    font-size: 11px;
    letter-spacing: 0.04em;
    opacity: 0.88;
}

// Full-bleed mobile banner: same image the desktop pagehead__pic shows, but edge-to-edge
// and moved below the board selector instead of squeezed beside the title.
.boards__mobile-pic {
    position: relative;
    margin: 0 calc(-1 * var(--page-x)) 24px;
    aspect-ratio: 16 / 9;
    background-size: cover;
    background-position: center;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 8px 10px;
}
.boards__mobile-pic::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, transparent 38%, rgba(0, 0, 0, 0.78) 100%);
}

@media (max-width: 991px) {
    ::v-deep .pagehead__pic {
        display: none;
    }
}

.boards__lucra {
    position: fixed;
    inset: 0;
    z-index: 3000;
}
.boards__lucra:empty {
    display: none;
}

@media (max-width: 575.98px) {
    .boards__lbhead,
    .boards__row {
        grid-template-columns: 28px minmax(0, 1fr) auto;
        gap: 10px;
        padding-left: 12px;
        padding-right: 12px;
    }
    .boards__row .sa-lb__driver {
        white-space: normal;
        overflow: visible;
        line-height: 1.25;
    }
}
</style>
